/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import { AfterViewInit, Component, EventEmitter, inject, Input, Output, signal, ChangeDetectionStrategy } from '@angular/core'
import { BaseFormControlComponent } from '../base-form-control.component'
import { InputSearchInputEventDetail, SelectChangeEventDetail, SelectToggleEventDetail } from '@porsche-design-system/components-angular'
import { AutocompleteAddress, AutocompleteService } from './autocomplete.service'
import { DynamicFormLayout } from '../../../service/dynamic-form-layout.service'
import { UntypedFormGroup } from '@angular/forms'
import { DynamicFormControlLayout } from '../../../model/misc/dynamic-form-control-layout.model'
import { DynamicSelectModel } from '../../../model/select/dynamic-select.model'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

@Component({
  selector: 'dynamic-basic-autocomplete',
  templateUrl: './dynamic-basic-autocomplete.component.html',
  standalone: false,
  // Angular 22 changed the implicit default to OnPush (see angular/angular#67687). This component was never migrated
  // to OnPush semantics, so Eager restores the pre-v22 behaviour it relies on.
  // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class DynamicBasicAutocompleteComponent extends BaseFormControlComponent implements AfterViewInit {
  @Input({ required: true }) formLayout: DynamicFormLayout = {}
  @Input({ required: true }) group: UntypedFormGroup = new UntypedFormGroup({})
  @Input({ required: true }) layout: DynamicFormControlLayout = {}
  @Input({ required: true }) model: DynamicSelectModel<string> = new DynamicSelectModel<string>({ id: '', options: [] })
  @Input({ required: true }) token = ''

  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() blur: EventEmitter<unknown> = new EventEmitter()
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() change: EventEmitter<unknown> = new EventEmitter()
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() focus: EventEmitter<unknown> = new EventEmitter()
  @Output() metaDataSelectedEvent: EventEmitter<unknown> = new EventEmitter()

  private autocompleteService = inject(AutocompleteService)

  value?: string
  initialLoading = signal<boolean>(true)
  loading = signal<boolean>(false)
  error = signal<boolean>(false)
  options = signal<{ value: string; label: string }[]>([])
  searchValue = signal<string>('')

  constructor() {
    super()
    this.autocompleteService.results$.pipe(takeUntilDestroyed()).subscribe((results) => this.options.set(results))
    this.autocompleteService.loading$.pipe(takeUntilDestroyed()).subscribe((loading) => this.loading.set(loading))
    this.autocompleteService.error$.pipe(takeUntilDestroyed()).subscribe((error) => this.error.set(error))
  }

  ngAfterViewInit() {
    if (!this.model.autocompleteCountry) {
      throw new Error('Autocomplete needs to have a autocompleteCountry attribute')
    } else {
      this.autocompleteService.addressCountry = this.model.autocompleteCountry
    }

    if (this.token) {
      this.autocompleteService.accessToken = this.token
    }
  }

  onInput(event: CustomEvent<InputSearchInputEventDetail>) {
    const term = (event.target as HTMLInputElement).value
    this.searchValue.set(term)
    if (term.length >= 4) {
      this.initialLoading.set(false)
      this.options.set([])
      this.autocompleteService.search(term)
    }
  }

  onChange(event: CustomEvent<SelectChangeEventDetail>) {
    this.value = event.detail.value as string

    if (this.value) {
      this.autocompleteService.fetchAutocompleteAddressDataForAddressKey(this.value).subscribe((data: AutocompleteAddress) => {
        this.metaDataSelectedEvent.emit(data)
      })
    }
  }

  onToggle(event: CustomEvent<SelectToggleEventDetail>) {
    if (event.detail.open) {
      this.searchValue.set('')
    }
  }
}
